import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Play } from "lucide-react";
import { useCollection } from "../useData";
import { resolveImg } from "../api";
import SEO from "../components/SEO";
import NotFound from "../components/NotFound";

const FilmDetail = () => {
  const { id } = useParams();
  const films = useCollection("films") || [];
  const film = films.find((f) => f.id === id);
  const [playing, setPlaying] = useState(false);

  // Reset playback state when navigating to a different film.
  useEffect(() => { setPlaying(false); }, [id]);

  if (!films.length) return null;
  if (!film) return <NotFound section="This film" backTo="/films" backLabel="Back to films" />;

  const currentIdx = films.findIndex((f) => f.id === film.id);
  const nextFilm = films[(currentIdx + 1) % films.length];

  return (
    <div className="px-6 md:px-10 max-w-[1100px] mx-auto">
      <SEO
        title={film.title}
        description={film.description || `${film.title} — ${film.subtitle || "film"} by Alice Lisi.`}
        image={film.cover}
        path={`/films/${film.id}`}
      />
      <div className="pt-2 pb-6 flex items-baseline justify-between">
        <Link to="/films" className="font-serif tlink tlink-mute">← Index</Link>
        <div className="flex items-baseline gap-4">
          <span className="font-serif italic text-xl">{film.title}</span>
          <span className="font-serif text-[color:var(--ink-mute)]">{film.subtitle} — {film.year}</span>
        </div>
      </div>

      {/* Video / poster — 16:9 natural film ratio */}
      <div className="max-w-[900px] mx-auto">
        <div className="relative w-full aspect-video bg-black overflow-hidden fade-in">
          {playing && film.youtubeId ? (
            <iframe
              title={film.title}
              src={`https://www.youtube.com/embed/${film.youtubeId}?autoplay=1&rel=0`}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              {film.cover ? (
                <img src={resolveImg(film.cover)} alt={film.title} className="w-full h-full object-cover opacity-90" />
              ) : null}
              {film.youtubeId ? (
                <button
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center group"
                  aria-label="Play film"
                >
                  <span className="w-24 h-24 rounded-full bg-[color:var(--paper)]/95 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Play className="w-8 h-8 ml-1" strokeWidth={1.5} fill="currentColor" />
                  </span>
                  <span className="absolute bottom-6 left-6 font-serif italic bg-[color:var(--paper)]/90 px-3 py-1.5">Watch film</span>
                </button>
              ) : (
                film.externalUrl && (
                  <a
                    href={film.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute inset-0 flex items-center justify-center group"
                    aria-label="Watch on external site"
                  >
                    <span className="px-6 py-3 bg-[color:var(--paper)]/95 font-serif tracking-wider text-sm group-hover:bg-[color:var(--paper)] transition-colors">
                      ▶ Watch on alicelisi.com
                    </span>
                  </a>
                )
              )}
            </>
          )}
        </div>

        <div className="mt-4 flex items-baseline justify-between">
          <span className="font-serif text-[color:var(--ink-mute)] counter">{film.duration}</span>
          <span className="font-serif italic text-[color:var(--ink-mute)]">Free to watch</span>
        </div>
      </div>

      {/* Description — no label */}
      <div className="mt-20 max-w-[900px] mx-auto">
        <h1 className="font-serif italic text-3xl md:text-4xl leading-[1.15] mb-4">{film.title}</h1>
        <p className="font-serif text-xl md:text-2xl leading-[1.35] whitespace-pre-line">{film.description}</p>
      </div>

      {/* Credits — no label */}
      <div className="mt-16 max-w-[900px] mx-auto pb-4">
        <dl className="grid grid-cols-12 gap-y-2 font-serif">
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Year</dt><dd className="col-span-8">{film.year}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Format</dt><dd className="col-span-8">{film.subtitle}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Duration</dt><dd className="col-span-8 counter">{film.duration}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Director</dt><dd className="col-span-8">{film.director}</dd>
          {Array.isArray(film.credits) && film.credits.map((c, i) => (
            <React.Fragment key={i}>
              <dt className="col-span-4 text-[color:var(--ink-mute)]">{c.role}</dt>
              <dd className="col-span-8">{c.name}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>

      {/* Next */}
      <div className="mt-32 pt-8 border-t border-[color:var(--line)] flex items-baseline justify-between">
        <span className="font-serif italic text-[color:var(--ink-mute)]">Next</span>
        <Link to={`/films/${nextFilm.id}`} className="font-serif italic text-2xl tlink">{nextFilm.title} →</Link>
      </div>
    </div>
  );
};

export default FilmDetail;
